import React, { useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import {
  Box, Drawer, List, ListItem, ListItemIcon,
  ListItemText, Typography, AppBar, Toolbar,
  IconButton, Card, CardContent, Grid, Divider
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import VerifiedIcon from "@mui/icons-material/Verified";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SecurityIcon from "@mui/icons-material/Security";
import LogoutIcon from "@mui/icons-material/Logout";
import ChecklistIcon from "@mui/icons-material/Checklist";

const DRAWER_WIDTH = 240;

const menuItems = [
  { text: "Dashboard",            icon: <DashboardIcon />,     id: "dashboard" },
  { text: "Employee Management",  icon: <PeopleIcon />,        id: "employees" },
  { text: "Onboarding Checklist", icon: <ChecklistIcon />,     id: "onboarding" },
  { text: "Certifications",       icon: <VerifiedIcon />,      id: "certifications" },
  { text: "Alerts & Notifications",icon: <NotificationsIcon />,id: "alerts" },
  { text: "Compliance Reports",   icon: <AssessmentIcon />,    id: "reports" },
];

const stats = [
  { label: "Total Employees",        value: "0", color: "#1a237e" },
  { label: "Active Certifications",  value: "0", color: "#1b5e20" },
  { label: "Expiring Soon",          value: "0", color: "#e65100" },
  { label: "Pending Onboarding",     value: "0", color: "#4a148c" },
];

export default function AdminDashboard({ user }) {
  const [activePage, setActivePage] = useState("dashboard");

  const handleLogout = async () => {
    await signOut(auth);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar */}
      <Drawer variant="permanent" sx={{
        width: DRAWER_WIDTH,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          bgcolor: "#1a237e",
          color: "white",
        }
      }}>
        {/* Logo */}
        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <SecurityIcon sx={{ color: "white", fontSize: 30 }} />
          <Typography variant="h6" fontWeight="bold" color="white" fontSize={14}>
            HQ Scan Security
          </Typography>
        </Box>
        <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />

        {/* Menu */}
        <List>
          {menuItems.map((item) => (
            <ListItem button key={item.id}
              onClick={() => setActivePage(item.id)}
              sx={{
                bgcolor: activePage === item.id
                  ? "rgba(255,255,255,0.2)" : "transparent",
                borderRadius: 2, mx: 1, mb: 0.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
              }}>
              <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text}
                primaryTypographyProps={{ fontSize: 13 }} />
            </ListItem>
          ))}
        </List>

        {/* Logout */}
        <Box sx={{ mt: "auto", p: 2 }}>
          <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 1 }} />
          <ListItem button onClick={handleLogout}
            sx={{ borderRadius: 2, "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}>
            <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout"
              primaryTypographyProps={{ fontSize: 13 }} />
          </ListItem>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        {/* Top Bar */}
        <AppBar position="static" elevation={0}
          sx={{ bgcolor: "white", color: "black" }}>
          <Toolbar>
            <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
              {menuItems.find(m => m.id === activePage)?.text || "Dashboard"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Dashboard Content */}
        <Box sx={{ p: 3 }}>
          {activePage === "dashboard" && (
            <>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Welcome back, Admin 👋
              </Typography>

              {/* Stats Cards */}
              <Grid container spacing={3} mb={4}>
                {stats.map((stat) => (
                  <Grid item xs={12} sm={6} md={3} key={stat.label}>
                    <Card elevation={2} sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                        <Typography variant="h3" fontWeight="bold"
                          sx={{ color: stat.color, mt: 1 }}>
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Placeholder for charts */}
              <Card elevation={2} sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Compliance Overview
                </Typography>
                <Box sx={{
                  height: 200, bgcolor: "#f0f4ff",
                  borderRadius: 2, display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <Typography color="text.secondary">
                    Charts will appear here as data is added
                  </Typography>
                </Box>
              </Card>
            </>
          )}

          {activePage !== "dashboard" && (
            <Box sx={{
              display: "flex", alignItems: "center",
              justifyContent: "center", height: "60vh",
              flexDirection: "column", gap: 2
            }}>
              <Typography variant="h5" color="text.secondary">
                {menuItems.find(m => m.id === activePage)?.text}
              </Typography>
              <Typography color="text.secondary">
                This module is coming soon...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}